import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Icon from './Icon';
import TherapistKnowledge from './TherapistKnowledge';
import TherapistPreferences from './TherapistPreferences';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const TherapyDashboard = ({ token, onPatientSelect, user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('patients');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  
  // State for data
  const [patients, setPatients] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Invite form state
  const [inviteForm, setInviteForm] = useState({
    patient_email: '',
    personal_message: ''
  });

  // Move helper functions before useMemo hooks
  const highlightMatch = (text, searchTerm) => {
    if (!text || !searchTerm) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? 
        `<mark class="bg-yellow-200 text-yellow-900 px-1 rounded">${part}</mark>` : 
        part
    ).join('');
  };

  const getPatientInitials = (patient) => {
    if (!patient.name) return 'P';
    const names = patient.name.split(' ');
    return names.map(name => name.charAt(0)).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (patient) => {
    if (patient.is_active === true) {
      return 'text-green-600 bg-green-100';
    } else if (patient.is_active === false) {
      return 'text-gray-600 bg-gray-100';
    } else {
      return 'text-blue-600 bg-blue-100';
    }
  };

  const getStatusText = (patient) => {
    if (patient.is_active === true) {
      return 'active';
    } else if (patient.is_active === false) {
      return 'inactive';
    } else {
      return 'unknown';
    }
  };

  const getInviteStatusColor = (status) => {
    switch(status) {
      case 'active': return 'bg-yellow-100 text-yellow-800';
      case 'used': return 'bg-green-100 text-green-800';
      case 'expired': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  useEffect(() => {
    loadData();
  }, [token]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadPatients(), loadInvitations()]);
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadPatients = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('🔄 Loading patients for therapist dashboard...');
      
      const response = await axios.get(`${API_BASE}/api/invitations/my-patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('✅ Patients loaded in dashboard:', response.data);
      
      if (response.data && response.data.patients) {
        setPatients(response.data.patients);
        console.log(`✅ Set ${response.data.patients.length} patients in state`);
      } else {
        console.log('⚠️ No patients data in response structure');
        setPatients([]);
      }
      
    } catch (err) {
      console.error('❌ Error loading patients in dashboard:', err);
      console.error('❌ Error details:', err.response?.data);
      
      // Check if it's a session expiration (401/403 errors)
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('SESSION_EXPIRED');
      } else {
        setError('Kunde inte ladda patienter: ' + (err.response?.data?.detail || err.message));
      }
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  const loadInvitations = async () => {
    try {
      console.log('🔄 Loading invitations...');
      const response = await axios.get(`${API_BASE}/api/invitations/my-invitations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('✅ Invitations loaded:', response.data);
      setInvitations(response.data || []);
    } catch (err) {
      console.error('❌ Error loading invitations:', err);
    }
  };

  const filteredPatients = useMemo(() => {
    return patients.filter(patient => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = searchTerm === '' || 
        patient.name?.toLowerCase().includes(searchLower) ||
        patient.email?.toLowerCase().includes(searchLower) ||
        patient.first_name?.toLowerCase().includes(searchLower) ||
        patient.last_name?.toLowerCase().includes(searchLower);
      
      return matchesSearch;
    });
  }, [patients, searchTerm]);

  const searchSuggestions = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) return [];
    
    const suggestions = patients
      .filter(patient => {
        const searchLower = searchTerm.toLowerCase();
        return patient.name?.toLowerCase().includes(searchLower) ||
               patient.first_name?.toLowerCase().includes(searchLower) ||
               patient.last_name?.toLowerCase().includes(searchLower);
      })
      .slice(0, 5)
      .map(patient => ({
        id: patient.id,
        name: patient.name,
        email: patient.email,
        highlightedName: highlightMatch(patient.name, searchTerm)
      }));
    
    return suggestions;
  }, [patients, searchTerm]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowSearchSuggestions(value.length >= 2);
  };

  const handleSearchFocus = () => {
    if (searchTerm.length >= 2) {
      setShowSearchSuggestions(true);
    }
  };

  const handleSearchBlur = () => {
    setTimeout(() => setShowSearchSuggestions(false), 200);
  };

  const selectSearchSuggestion = (patient) => {
    setSearchTerm(patient.name);
    setShowSearchSuggestions(false);
    setSelectedPatient(patient);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setShowSearchSuggestions(false);
  };

  const handlePatientSelect = (patient) => {
    // Instead of setting selectedPatient, call onPatientSelect to route to details
    if (onPatientSelect) {
      onPatientSelect(patient);
    }
  };

  const handlePatientCardClick = (patient) => {
    // Route to patient details page
    handlePatientSelect(patient);
  };

  const handleInviteSubmit = async () => {
    if (!inviteForm.patient_email) {
      setError('Please enter patient email');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(
        `${API_BASE}/api/invitations/create-invitation`,
        inviteForm,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setSuccess(`Invitation created! Code: ${response.data.invitation_code}`);
      setInviteForm({ patient_email: '', personal_message: '' });
      setShowInviteForm(false);
      await loadInvitations();
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create invitation');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Invitation code copied to clipboard!');
    setTimeout(() => setSuccess(''), 3000);
  };

  if (loading && patients.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-64 bg-white rounded-lg shadow-sm">
        <div className="text-center">
          <Icon name="loading" size={32} className="animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-600 text-lg">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error === 'SESSION_EXPIRED') {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <Icon name="alert-triangle" size={48} className="text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Session har gått ut</h3>
          <p className="text-sm text-gray-500 mb-6">
            Din session har gått ut av säkerhetsskäl. Logga in igen för att fortsätta.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.href = '/login'}
              className="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors"
            >
              <Icon name="user" size={16} className="mr-2" />
              Logga in igen
            </button>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              <Icon name="refresh" size={16} className="mr-2" />
              Försök igen
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error && error !== 'SESSION_EXPIRED') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800 mb-3">⚠️ {error}</p>
        <button 
          onClick={loadPatients}
          className="px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors font-medium"
        >
          Försök igen
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Success/Error Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-red-800">⚠️ {error}</p>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <p className="text-green-800">✅ {success}</p>
        </div>
      )}

      <div className="max-w-7xl mx-auto ">
        {/* Navigation Tabs */}
        <div className="mb-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('patients')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === 'patients'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon name="users" size={16} className="inline mr-2" />
              Klienter ({patients.length})
            </button>
            <button
              onClick={() => setActiveTab('invites')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === 'invites'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon name="mail" size={16} className="inline mr-2" />
              Inbjudningar ({invitations.length})
            </button>
            {/* <button
              onClick={() => setActiveTab('knowledge')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === 'knowledge'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon name="bot-message-square" size={16} className="inline mr-2" />
              AI Kunskapsbas
            </button> */}
            <button
              onClick={() => setActiveTab('preferences')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === 'preferences'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon name="settings" size={16} className="inline mr-2" />
              AI inställningar
            </button>
          </nav>
        </div>

        {activeTab === 'patients' && (
          <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Klienter</h3>
                    <button
                      onClick={() => setShowInviteForm(true)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                    >
                      <Icon name="plus" size={16} className="mr-1" />
                      Bjud in
                    </button>
                  </div>

                  {/* Enhanced Search */}
                  <div className="space-y-3 mb-4">
                    <div className="relative">
                      <Icon name="search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Sök klienter efter namn..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onFocus={handleSearchFocus}
                        onBlur={handleSearchBlur}
                        className="pl-10 pr-10 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />

                      {/* Clear button */}
                      {searchTerm && (
                        <button
                          onClick={clearSearch}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <Icon name="x" size={16} />
                        </button>
                      )}

                      {/* Search Suggestions Dropdown */}
                      {showSearchSuggestions && searchSuggestions.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                          <div className="py-1">
                            <div className="px-3 py-2 text-xs font-medium text-gray-500 bg-gray-50 border-b">
                              Sökresultat ({searchSuggestions.length})
                            </div>
                            {searchSuggestions.map((suggestion) => (
                              <button
                                key={suggestion.id}
                                onClick={() => selectSearchSuggestion(suggestion)}
                                className="w-full text-left px-3 py-2 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                              >
                                <div className="flex items-center space-x-3">
                                  <div className="w-8 h-8 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-semibold text-xs">
                                    {getPatientInitials({ name: suggestion.name })}
                                  </div>
                                  <div className="flex-1">
                                    <div 
                                      className="font-medium text-sm"
                                      dangerouslySetInnerHTML={{ __html: suggestion.highlightedName }}
                                    />
                                    <div className="text-xs text-gray-500 truncate">
                                      {suggestion.email}
                                    </div>
                                  </div>
                                  <Icon name="arrow-right" size={12} className="text-gray-400" />
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* No Results Found */}
                      {showSearchSuggestions && searchTerm.length >= 2 && searchSuggestions.length === 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                          <div className="py-3 px-3 text-center">
                            <Icon name="search" size={16} className="mx-auto text-gray-400 mb-2" />
                            <p className="text-sm text-gray-500">
                              Inga klienter hittade som matchar "{searchTerm}"
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Search Results Summary */}
                  {searchTerm && (
                    <div className="mb-4 p-2 bg-blue-50 rounded-md text-sm text-blue-700">
                      <div className="flex items-center justify-between">
                        <span>
                          {filteredPatients.length} klient{filteredPatients.length !== 1 ? 'er' : ''} hittade
                          {searchTerm && ` för "${searchTerm}"`}
                        </span>
                        <button
                          onClick={clearSearch}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Rensa
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Patient List */}
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {filteredPatients.length === 0 ? (
                      <div className="text-center py-8">
                        <Icon name="users" size={48} className="mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-600">
                          {searchTerm ? 
                            `Inga klienter hittade som matchar "${searchTerm}".` : 
                            'Inga klienter anslutna ännu.'
                          }
                        </p>
                        {searchTerm && (
                          <button
                            onClick={clearSearch}
                            className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Rensa sökning för att se alla klienter
                          </button>
                        )}
                      </div>
                    ) : (
                      filteredPatients.map((patient) => (
                        <div
                          key={patient.id}
                          className={`p-3 rounded-lg border transition-all duration-200 cursor-pointer hover:bg-gray-100 hover:border-blue-300 hover:shadow-md relative`}
                          onClick={() => handlePatientCardClick(patient)}
                        >
                          {/* Smart notification indicator */}
                          {patient.has_recent_insights && (
                            <div className="absolute top-2 right-2">
                              <div className="w-3 h-3 bg-orange-400 rounded-full animate-pulse"></div>
                            </div>
                          )}
                          
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-semibold text-sm">
                              {getPatientInitials(patient)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-gray-900 text-sm truncate">
                                {searchTerm ? (
                                  <span dangerouslySetInnerHTML={{ 
                                    __html: highlightMatch(patient.name, searchTerm) 
                                  }} />
                                ) : (
                                  patient.name
                                )}
                              </h4>
                              <p className="text-xs text-gray-500 truncate">{patient.email}</p>
                              
                              {/* Show insight indicators */}
                              {patient.recent_activity && (
                                <div className="flex items-center mt-1 space-x-2">
                                  {patient.recent_activity.breakthrough_count > 0 && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                      <Icon name="zap" size={10} className="mr-1" />
                                      {patient.recent_activity.breakthrough_count} genombrott
                                    </span>
                                  )}
                                  {patient.recent_activity.important_messages > 0 && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      <Icon name="lightbulb" size={10} className="mr-1" />
                                      {patient.recent_activity.important_messages} insikter
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center text-gray-400">
                              <Icon name="chevron-right" size={16} />
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
        )}

        {activeTab === 'invites' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">Klientinbjudningar</h3>
                <button
                  onClick={() => setShowInviteForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                >
                  <Icon name="plus" size={16} className="mr-2" />
                  Ny Inbjudning
                </button>
              </div>

              {invitations.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="mail" size={48} className="mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Inga Inbjudningar Ännu</h3>
                  <p className="text-sm text-gray-500 mb-4">Skapa din första klientinbjudning för att komma igång.</p>
                  <button
                    onClick={() => setShowInviteForm(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                  >
                    <Icon name="plus" size={16} className="mr-2" />
                    Skapa Inbjudning
                  </button>
                </div>
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invitation Code</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
                        <th className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {invitations.map((invite) => (
                        <tr key={invite.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{invite.patient_email}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className="text-sm font-mono text-gray-700 bg-gray-100 px-2 py-1 rounded">
                                {invite.invitation_code}
                              </span>
                              <button 
                                onClick={() => copyToClipboard(invite.invitation_code)}
                                className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                                title="Copy invitation code"
                              >
                                <Icon name="copy" size={16} />
                              </button>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getInviteStatusColor(invite.status)}`}>
                              {invite.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(invite.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(invite.expires_at)}
                          </td>
                          {/* <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button 
                              onClick={() => copyToClipboard(invite.invitation_code)}
                              className="text-blue-600 hover:text-blue-900 transition-colors"
                            >
                              <Icon name="copy" size={16} />
                            </button>
                          </td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Invite Form Modal */}
      {showInviteForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Skicka Klientinbjudning</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Klientens E-post *
                </label>
                <input
                  type="email"
                  id="email"
                  value={inviteForm.patient_email}
                  onChange={(e) => setInviteForm({...inviteForm, patient_email: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="klient@email.com"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Personligt Meddelande (Valfritt)
                </label>
                <textarea
                  id="message"
                  rows={3}
                  value={inviteForm.personal_message}
                  onChange={(e) => setInviteForm({...inviteForm, personal_message: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Välkommen! Jag ser fram emot att arbeta med dig..."
                />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-xs text-blue-800">
                    <strong>Info:</strong> Inbjudningen gäller i 7 dagar från skapande. Efter det måste en ny inbjudan skapas.
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowInviteForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  onClick={handleInviteSubmit}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  <Icon name="send" size={16} className="mr-2" />
                  {loading ? 'Skickar...' : 'Skicka Inbjudning'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'knowledge' && (
        <TherapistKnowledge token={token} />
      )}

      {activeTab === 'preferences' && (
        <TherapistPreferences token={token} />
      )}
    </div>
  );
}

export default TherapyDashboard;